import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';

const { FiArrowLeft, FiSettings, FiFolder, FiCalendar, FiDollarSign, FiShield, FiFileText, FiClock, FiUsers } = FiIcons;

const CoreLifecycle = () => {
  const [selectedArea, setSelectedArea] = useState('personnel');

  const lifecycleAreas = {
    personnel: {
      title: 'Personnel File & GDPR Compliance',
      subtitle: 'Secure employee data management',
      timeline: 'Ongoing',
      areas: [
        {
          area: 'Personnel File Setup & Maintenance',
          urgency: 'critical',
          icon: FiFolder,
          actions: [
            '**Create secure personnel file** (physical and digital)',
            'Implement **data classification** system (confidential/restricted/public)',
            'Set up **access controls** with audit trail logging',
            'Establish **file retention schedule** per GDPR requirements',
            'Create **data subject access** request procedures'
          ],
          compliance: [
            { type: 'legal', text: 'GDPR Article 5: Data must be processed lawfully, fairly and transparently' },
            { type: 'legal', text: 'DPA 2018: Employers must have lawful basis for processing employee data' }
          ]
        },
        {
          area: 'Data Protection & Privacy',
          urgency: 'critical',
          icon: FiShield,
          actions: [
            'Issue **privacy notices** explaining data processing',
            'Obtain **explicit consent** for non-essential processing',
            'Implement **data minimization** - collect only necessary data',
            'Set up **automated data retention** and deletion schedules',
            'Create **breach notification** procedures (72-hour rule)'
          ],
          compliance: [
            { type: 'legal', text: 'GDPR Article 13: Privacy information must be provided at data collection' },
            { type: 'legal', text: 'GDPR Article 33: Data breaches must be reported within 72 hours' }
          ]
        },
        {
          area: 'Document Management & Audit',
          urgency: 'high',
          icon: FiFileText,
          actions: [
            'Maintain **chronological employment record**',
            'Document all **significant employment events**',
            'Create **document version control** system',
            'Implement **regular audit schedule** (quarterly)',
            'Set up **data subject rights** fulfillment process'
          ],
          compliance: [
            { type: 'legal', text: 'GDPR Article 15: Individuals have right to access their personal data' },
            { type: 'acas', text: 'Good record keeping essential for defending tribunal claims' }
          ]
        }
      ]
    },
    absence: {
      title: 'Holiday & Absence Management',
      subtitle: 'Comprehensive leave tracking and compliance',
      timeline: 'Ongoing',
      areas: [
        {
          area: 'Holiday Entitlement & Booking',
          urgency: 'high',
          icon: FiCalendar,
          actions: [
            'Calculate **statutory minimum** (5.6 weeks including bank holidays)',
            'Set up **holiday year** and carry-over policies',
            'Implement **advance booking** system with approval workflow',
            'Create **holiday pay calculation** (including overtime/commission)',
            'Establish **notice periods** for holiday requests'
          ],
          compliance: [
            { type: 'legal', text: 'Working Time Regulations 1998: Minimum 5.6 weeks annual leave' },
            { type: 'legal', text: 'Employment Rights Act 1996: Holiday pay must include regular overtime' }
          ]
        },
        {
          area: 'Sickness Absence Management',
          urgency: 'critical',
          icon: FiUsers,
          actions: [
            'Set up **self-certification** process (up to 7 days)',
            'Require **fit notes** for absences over 7 days',
            'Implement **return to work interviews** (within 24-48 hours)',
            'Create **absence monitoring** triggers and intervention points',
            'Establish **occupational health** referral procedures'
          ],
          compliance: [
            { type: 'legal', text: 'Statutory Sick Pay (SSP) regulations: Minimum payments required' },
            { type: 'legal', text: 'Equality Act 2010: Reasonable adjustments for disability-related absence' }
          ]
        },
        {
          area: 'Special Leave & Statutory Rights',
          urgency: 'medium',
          icon: FiClock,
          actions: [
            'Set up **maternity/paternity leave** calculation and tracking',
            'Implement **shared parental leave** administration',
            'Create **time off for dependants** emergency leave policy',
            'Establish **jury service** and **reserve forces** leave',
            'Set up **bereavement leave** (statutory and enhanced)'
          ],
          compliance: [
            { type: 'legal', text: 'Employment Rights Act 1996: Various statutory leave entitlements' },
            { type: 'legal', text: 'Parental Bereavement (Leave and Pay) Act 2020: 2 weeks leave for child loss' }
          ]
        },
        {
          area: 'Absence Analytics & Intervention',
          urgency: 'medium',
          icon: FiSettings,
          actions: [
            'Generate **absence statistics** and trend analysis',
            'Set **trigger points** for absence management action',
            'Create **capability procedures** for excessive absence',
            'Implement **wellness programs** and absence reduction initiatives',
            'Monitor **patterns** and potential abuse of absence policies'
          ],
          compliance: [
            { type: 'acas', text: 'Fair absence management requires clear policies and consistent application' },
            { type: 'legal', text: 'Disability discrimination: Consider reasonable adjustments before capability action' }
          ]
        }
      ]
    },
    payroll: {
      title: 'Payroll & HMRC Compliance',
      subtitle: 'Accurate payment and tax administration',
      timeline: 'Monthly/Ongoing',
      areas: [
        {
          area: 'Payroll Setup & Processing',
          urgency: 'critical',
          icon: FiDollarSign,
          actions: [
            'Set up **PAYE scheme** with HMRC registration',
            'Implement **RTI (Real Time Information)** reporting system',
            'Create **payroll calendar** with submission deadlines',
            'Set up **automatic tax code updates** from HMRC',
            'Implement **payslip distribution** (physical or digital)'
          ],
          compliance: [
            { type: 'legal', text: 'Income Tax (PAYE) Regulations 2003: Real-time reporting required' },
            { type: 'legal', text: 'Employment Rights Act 1996: Itemized payslips must be provided' }
          ]
        },
        {
          area: 'Statutory Payments & Deductions',
          urgency: 'critical',
          icon: FiFileText,
          actions: [
            'Calculate **National Insurance** contributions (employer/employee)',
            'Process **Statutory Sick Pay (SSP)** calculations',
            'Handle **Statutory Maternity/Paternity Pay** claims',
            'Manage **student loan deductions** and repayments',
            'Process **court orders** (attachment of earnings)'
          ],
          compliance: [
            { type: 'legal', text: 'Social Security Contributions and Benefits Act 1992: NI contribution requirements' },
            { type: 'legal', text: 'Statutory Sick Pay (General) Regulations 1982: SSP calculation rules' }
          ]
        },
        {
          area: 'Year-End Compliance & Reporting',
          urgency: 'high',
          icon: FiCalendar,
          actions: [
            'Prepare **P60 forms** for all employees (by 31 May)',
            'Issue **P45 forms** for leavers within specified timeframes',
            'Submit **P11D forms** for benefits in kind (by 6 July)',
            'File **annual return** and pay Class 1A NI on benefits',
            'Reconcile **payroll records** with HMRC submissions'
          ],
          compliance: [
            { type: 'legal', text: 'Income Tax (PAYE) Regulations 2003: Annual reporting deadlines' },
            { type: 'legal', text: 'Penalties apply for late submission of returns and payments' }
          ]
        }
      ]
    },
    pensions: {
      title: 'Pension Auto-Enrolment',
      subtitle: 'Workplace pension compliance',
      timeline: 'Ongoing',
      areas: [
        {
          area: 'Auto-Enrolment Assessment',
          urgency: 'critical',
          icon: FiUsers,
          actions: [
            'Assess **eligible jobholders** (aged 22+ earning £10,000+)',
            'Identify **non-eligible** and **entitled workers**',
            'Set up **automatic enrolment** triggers for new starters',
            'Implement **re-enrolment** process every 3 years',
            'Create **opt-out** procedures and cooling-off periods'
          ],
          compliance: [
            { type: 'legal', text: 'Pensions Act 2008: Auto-enrolment within 3 months of employment' },
            { type: 'legal', text: 'Pensions Regulator: Fines up to £50,000 for non-compliance' }
          ]
        },
        {
          area: 'Pension Scheme Management',
          urgency: 'high',
          icon: FiSettings,
          actions: [
            'Select **qualifying pension scheme** meeting minimum standards',
            'Set up **contribution rates** (minimum 3% employer, 5% employee)',
            'Implement **payroll integration** for contribution deductions',
            'Create **member communications** and annual statements',
            'Manage **scheme changes** and member transfers'
          ],
          compliance: [
            { type: 'legal', text: 'Pensions Act 2008: Minimum contribution rates and scheme quality standards' },
            { type: 'legal', text: 'Occupational Pension Schemes Regulations: Scheme governance requirements' }
          ]
        },
        {
          area: 'Regulatory Compliance & Reporting',
          urgency: 'medium',
          icon: FiFileText,
          actions: [
            'Submit **declaration of compliance** to Pensions Regulator',
            'Maintain **auto-enrolment records** for audit purposes',
            'Process **contribution submissions** to pension provider',
            'Handle **regulatory correspondence** and inquiries',
            'Prepare for **Pensions Regulator inspections**'
          ],
          compliance: [
            { type: 'legal', text: 'Pensions Regulator: Regular compliance monitoring and potential penalties' },
            { type: 'acas', text: 'Good pension communication improves employee satisfaction' }
          ]
        }
      ]
    },
    benefits: {
      title: 'Benefits & CPD Management',
      subtitle: 'Employee development and benefits administration',
      timeline: 'Ongoing',
      areas: [
        {
          area: 'Benefits Administration',
          urgency: 'medium',
          icon: FiDollarSign,
          actions: [
            'Set up **flexible benefits** platform and enrollment',
            'Manage **health insurance** and medical benefits',
            'Administer **life assurance** and income protection',
            'Process **cycle to work** and salary sacrifice schemes',
            'Handle **childcare vouchers** and tax-efficient benefits'
          ],
          compliance: [
            { type: 'legal', text: 'Income Tax (Earnings and Pensions) Act 2003: Tax treatment of benefits' },
            { type: 'legal', text: 'P11D reporting required for most benefits in kind' }
          ]
        },
        {
          area: 'Training & Development Tracking',
          urgency: 'medium',
          icon: FiSettings,
          actions: [
            'Create **individual development plans** linked to role requirements',
            'Track **mandatory training** completion and renewals',
            'Manage **CPD requirements** for professional roles',
            'Set up **training budget** allocation and tracking',
            'Monitor **skills gaps** and training effectiveness'
          ],
          compliance: [
            { type: 'legal', text: 'Health and Safety at Work Act 1974: Adequate training required' },
            { type: 'acas', text: 'Investment in training improves retention and performance' }
          ]
        },
        {
          area: 'Career Development & Succession',
          urgency: 'low',
          icon: FiUsers,
          actions: [
            'Implement **career progression** frameworks and pathways',
            'Create **succession planning** for key roles',
            'Set up **mentoring programs** and knowledge transfer',
            'Track **internal mobility** and promotion rates',
            'Monitor **diversity** in development opportunities'
          ],
          compliance: [
            { type: 'acas', text: 'Fair access to development reduces discrimination risk' },
            { type: 'legal', text: 'Equality Act 2010: Equal access to training and development' }
          ]
        }
      ]
    }
  };

  const currentArea = lifecycleAreas[selectedArea];

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'critical': return 'bg-red-600';
      case 'high': return 'bg-orange-600';
      case 'medium': return 'bg-yellow-600';
      case 'low': return 'bg-green-600';
      default: return 'bg-gray-600';
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center mb-6">
        <Link to="/" className="mr-4">
          <SafeIcon icon={FiArrowLeft} className="text-2xl text-gray-600 hover:text-gray-900" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Core Employee Lifecycle Management</h1>
          <p className="text-gray-600">Comprehensive employee data and administrative processes</p>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {Object.keys(lifecycleAreas).map((area) => (
            <button
              key={area}
              onClick={() => setSelectedArea(area)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedArea === area 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {area === 'personnel' ? 'Personnel Files' :
               area === 'absence' ? 'Holiday & Absence' :
               area === 'payroll' ? 'Payroll & HMRC' :
               area === 'pensions' ? 'Pensions' : 'Benefits & CPD'}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{currentArea.title}</h2>
            <p className="text-gray-600">{currentArea.subtitle}</p>
          </div>
          <div className="text-right">
            <span className="text-sm text-purple-600 font-medium">Timeline</span>
            <p className="text-lg font-bold text-purple-800">{currentArea.timeline}</p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {currentArea.areas.map((area, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg shadow-lg overflow-hidden"
          >
            <div className={`${getUrgencyColor(area.urgency)} px-6 py-4`}>
              <div className="flex items-center space-x-3">
                <SafeIcon icon={area.icon} className="text-white text-2xl" />
                <div>
                  <h3 className="text-white font-semibold text-xl">{area.area}</h3>
                  <span className="text-white opacity-90 text-sm capitalize">
                    {area.urgency} Priority
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <SafeIcon icon={FiSettings} className="text-purple-600 mr-2" />
                    Required Actions
                  </h4>
                  <ul className="space-y-3">
                    {area.actions.map((action, actionIndex) => (
                      <li key={actionIndex} className="flex items-start space-x-2">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0" />
                        <span 
                          className="text-gray-700"
                          dangerouslySetInnerHTML={{ 
                            __html: action.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
                          }}
                        />
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <SafeIcon icon={FiShield} className="text-orange-600 mr-2" />
                    Compliance Requirements
                  </h4>
                  <div className="space-y-3">
                    {area.compliance.map((requirement, reqIndex) => (
                      <div key={reqIndex} className="flex items-start space-x-2">
                        <span className={`text-xs font-bold px-2 py-1 rounded flex-shrink-0 ${
                          requirement.type === 'legal' 
                            ? 'text-red-600 bg-red-50' 
                            : 'text-green-600 bg-green-50'
                        }`}>
                          {requirement.type === 'legal' ? '⚖️ Legal' : '🟢 ACAS'}
                        </span>
                        <span className="text-gray-700 text-sm">{requirement.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h4 className="font-semibold text-red-900 mb-3">⚖️ Critical Compliance Areas</h4>
          <ul className="text-sm text-red-800 space-y-2">
            <li>✓ GDPR data protection (£17.5M max fines)</li>
            <li>✓ RTI payroll reporting (penalties for late submission)</li>
            <li>✓ Auto-enrolment pensions (£50K max penalty)</li>
            <li>✓ Statutory leave entitlements</li>
            <li>✓ Working time regulations compliance</li>
          </ul>
        </div>

        <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
          <h4 className="font-semibold text-purple-900 mb-3">📊 Key Performance Indicators</h4>
          <ul className="text-sm text-purple-800 space-y-2">
            <li>• Data subject access response time (&lt;30 days)</li>
            <li>• Payroll accuracy rate (&gt;99.5%)</li>
            <li>• Absence rate monitoring (industry benchmarks)</li>
            <li>• Pension auto-enrolment compliance (100%)</li>
            <li>• Training completion rates by deadline</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CoreLifecycle;